/**
 * §P.10 · El control de peso de un alumno.
 *
 * ## Bloqueado para menores de 16, y el bloqueo es del servidor
 *
 * `422 weight_log_not_allowed_for_minor`, salvo que el club lo active expresamente en
 * sus ajustes. **En deportes con categorías de peso el riesgo de trastornos
 * alimentarios es real y documentado** (`brainstorming.md` §14), y por eso la regla no
 * es un botón escondido: si estuviera solo en la interfaz, el primer cliente de API
 * se la salta.
 *
 * Y lee el interruptor del club, no solo la edad: un club que lo ha activado
 * expresamente **tiene que poder usarlo**. Esconderle el formulario después de haber
 * tomado esa decisión sería una segunda regla, escrita en la interfaz, que contradice
 * a la del servidor.
 *
 * Lo que sí hace esta pantalla es **decirlo antes de que alguien escriba un número**.
 * Un `422` después de teclear es una regla que parece un fallo; dicha antes, es una
 * decisión del producto que el club entiende.
 *
 * ## Lo que importa es el `delta`, no el peso
 *
 * Un peso suelto no dice nada. **Tres kilos menos en dos semanas antes de un
 * campeonato** es exactamente lo que un maestro tiene que ver, y por eso la diferencia
 * va en grande y en rojo cuando baja de golpe.
 *
 * ## Pesar dos veces el mismo día corrige, no duplica
 *
 * En un pesaje oficial eso pasa constantemente. Lo resuelve el servidor y aquí solo se
 * dice, para que nadie evite corregir por miedo a ensuciar el historial.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha, numero } from '@/shared/ui/formato';
import type { PesoDelAlumno } from '@/club/competicion/tipos';

/**
 * Cuánto ha cambiado, con su signo y su color.
 *
 * Medio kilo arriba o abajo es ruido de báscula. **Dos kilos en cualquier dirección**
 * es lo que hace levantar la vista, y es donde empieza el rojo: en un adolescente que
 * compite, bajar dos kilos rápido es tan digno de mirar como subirlos.
 */
function cambio(delta: number | null): { texto: string; clase: string } | null {
  if (delta === null) return null;

  const signo = delta > 0 ? '+' : '';
  const clase = Math.abs(delta) >= 2 ? 'texto-mal' : 'tenue';
  return { texto: `${signo}${numero(delta, 1)} kg`, clase };
}

export function ControlDePeso({
  club,
  alumnoId,
  edad,
  puedeEditar,
}: {
  club: string;
  alumnoId: string;
  /** Para avisar **antes** de escribir, no después del `422`. */
  edad: number | null;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [peso, setPeso] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  /*
   * Los ajustes del club, para saber si ha activado el registro en menores. Es la
   * misma consulta que usa la pantalla de interruptores, así que la caché la sirve
   * sin volver a pedir nada.
   */
  const ajustes = useQuery({
    queryKey: ['ajustes', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).settings.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as { weight_logs_under_16: boolean };
    },
  });

  /*
   * **No se pide lo que el servidor va a negar** · 02-10-2026. Con los ajustes
   * leídos, un menor de 16 en un club que no lo ha activado ya se sabe que da `422`:
   * pedirlo de todos modos era una petición fallida en cada ficha de un niño, en
   * rojo en la consola del navegador, para pintar la misma nota.
   *
   * Si los ajustes no se pueden leer —un rol que no los ve—, se pide como antes y
   * el `422` se sigue leyendo como un estado (abajo). El bloqueo lo decide el
   * servidor en los dos casos; esto solo ahorra la pregunta.
   */
  const permitidoEnMenores = ajustes.data?.weight_logs_under_16 === true;
  const menor = edad !== null && edad < 16 && !permitidoEnMenores;
  const sabidoQueSeNiega = ajustes.isSuccess && menor;

  const pesos = useQuery({
    queryKey: ['pesos', club, alumnoId],
    enabled: !ajustes.isPending && !sabidoQueSeNiega,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        ['weight-logs'].get({ query: {} });

      /*
       * **El bloqueo de menores corta también la lectura**, no solo el `POST`, y eso
       * hay que leerlo como un estado y no como un fallo: un club que no lleva
       * control de peso no tiene un error, tiene una decisión tomada. Sin esto la
       * ficha de cualquier niño de diez años salía con un recuadro rojo de error
       * debajo de sus tallas. Visto en el navegador el 22-09-2026.
       */
      if (error) {
        const p = aProblema(error);
        if (p.code === 'weight_log_not_allowed_for_minor') return [];
        throw new ErrorDeApi(p);
      }
      return data as unknown as PesoDelAlumno[];
    },
  });

  const anotar = useMutation({
    mutationFn: async (kg: number) => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        ['weight-logs'].post({ weight_kg: kg });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setPeso('');
      setProblema(null);
      void cache.invalidateQueries({ queryKey: ['pesos', club, alumnoId] });
      // La sugerencia de categoría usa el último peso: si hay un campeonato
      // abierto en otra pestaña, su cruce se queda viejo.
      void cache.invalidateQueries({ queryKey: ['sugeridas', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (ajustes.isPending || (!sabidoQueSeNiega && pesos.isPending)) {
    return <Cargando filas={2} etiqueta="Cargando pesos" />;
  }

  if (!sabidoQueSeNiega && (pesos.isError || !pesos.data)) {
    return (
      <PanelError
        problema={aProblema(pesos.error)}
        reintentar={() => void pesos.refetch()}
      />
    );
  }

  const lista: PesoDelAlumno[] = sabidoQueSeNiega ? [] : (pesos.data ?? []);

  return (
    <>
      {problema && <PanelError problema={problema} />}

      {/*
        Dicho antes de teclear. El bloqueo lo aplica el servidor igualmente; lo que
        cambia es que aquí se explica por qué en vez de devolver un error que parece
        un fallo del programa.
      */}
      {menor && (
        <p className="nota">
          <strong>El control de peso no se lleva en menores de 16 años.</strong>{' '}
          En deportes con categorías de peso el riesgo de trastornos
          alimentarios es real, y hemos preferido no construir la herramienta
          que lo facilita. Un club que lo necesite de verdad puede activarlo en
          «El club» → «Ajustes», y entonces responde de ello.
        </p>
      )}

      {/*
        Y si lo ha activado, se dice también: que la decisión esté tomada no
        significa que quien tiene la pantalla delante la conozca.
      */}
      {edad !== null && edad < 16 && permitidoEnMenores && (
        <p className="tenue">
          Este club ha activado el registro de peso en menores de 16 años. Lo
          normal es no llevarlo.
        </p>
      )}

      {lista.length === 0 ? (
        <p className="tenue">
          No hay ningún peso anotado.{' '}
          {menor
            ? 'Y con menos de 16 años, no se va a poder anotar.'
            : 'Sirve para saber en qué categoría entra en un campeonato.'}
        </p>
      ) : (
        <ul className="lista-clara">
          {lista.map((p) => {
            const d = cambio(p.delta_kg);
            return (
              <li key={p.id}>
                <strong>{numero(p.weight_kg, 1)} kg</strong>
                {d && <span className={d.clase}> · {d.texto}</span>}
                <span className="tenue">
                  {' '}
                  · {fecha(p.measured_on)}
                  {p.competition_name && ` · ${p.competition_name}`}
                  {p.recorded_by_name && ` · lo anotó ${p.recorded_by_name}`}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {puedeEditar && !menor && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            const kg = Number(peso.replace(',', '.'));
            if (Number.isFinite(kg) && kg > 0) anotar.mutate(kg);
          }}
        >
          <div className="rejilla">
            <label>
              Peso de hoy (kg)
              <input
                inputMode="decimal"
                placeholder="48,5"
                value={peso}
                onChange={(e) => setPeso(e.target.value)}
              />
            </label>
          </div>

          <div className="acciones">
            <button
              type="submit"
              className="btn-primario"
              disabled={peso.trim() === '' || anotar.isPending}
            >
              {anotar.isPending ? 'Anotando…' : 'Anotar'}
            </button>
          </div>

          <p className="tenue">
            Pesar dos veces el mismo día corrige el dato, no lo duplica.
          </p>
        </form>
      )}
    </>
  );
}
